import { postRequest } from "lib/core/apiClient";
import Cookies from "js-cookie";
import { constants } from "lib/utils/constants";
import { OTPChannel, OTPVerificationDetails } from "lib/types/user";
import { jwtDecode } from "jwt-decode";
import { isThirdPartyCookieEnabled } from "./helpers";
import { CustomerConsentsPayload } from "lib/types/consent";

type AuthPayload = {
  context: "AUTH";
  requestContext?: "LOGIN" | "AUTH";
};

type SSOPayload = {
  context: "SSO";
  requestId: string;
};

type ProfilePayload = {
  context: "PROFILE";
};
export type OTPDeliveryType = AuthPayload | SSOPayload | ProfilePayload | AutomationPayload;

type AutomationPayload = {
  context: "NO_RECAPTCHA";
};

export const sendOtpWithPhoneNumber = async (
  details: OTPVerificationDetails,
  metada: OTPDeliveryType,
  reCaptchaToken: string = "",
  captchaProvider: "TURNSTILE" | "ALTCHA" = "TURNSTILE",
) => {
  const allowMissingMerchantId =
    (metada.context === "AUTH" && metada.requestContext === "LOGIN") || metada.context === "PROFILE";
  if (!details?.identifier || (!details?.merchantId && !allowMissingMerchantId)) return;

  try {
    const payload = {
      oid: details?.identifier,
      ...(details?.merchantId && { merchant_id: details.merchantId }),
      ...(metada.context === "AUTH" && {
        ...(metada.requestContext && { context: metada.requestContext }),
        skip_verification: details?.skipVerification ?? false,
        linked_user_id: details.userId ?? null,
        force_verification: details.forceVerification ?? false,
      }),
      ...(metada.context === "PROFILE" && {
        skip_verification: details?.skipVerification ?? false,
        // linked_user_id: details.userId ?? null,
        force_verification: true,
      }),
      ...(metada.context === "NO_RECAPTCHA" && {
        context: "NO_RECAPTCHA",
        skip_verification: details?.skipVerification ?? false,
        linked_user_id: details.userId ?? null,
        force_verification: details.forceVerification ?? false,
      }),
      ...(metada.context === "SSO" && { context: "SSO", sso_request_id: metada.requestId }),
      ...(Boolean(reCaptchaToken) && { captcha_token: reCaptchaToken }),
      ...(Boolean(reCaptchaToken) && { captcha_provider: captchaProvider }),
      // domain: details.domain ?? "",
    };
    const response = await postRequest(`/otp/send`, payload, "AUTH");
    return response;
  } catch (e: any) {
    console.error(e);
    throw new Error(e);
  }
};

export const verifyOtp = async (
  contextId: string,
  otp: string,
  skipTokenGeneration?: boolean,
  additionalPayload?: {
    bureau_event_id?: string;
    /** DPDPA consents ticked before login, saved as part of it */
    customer_consents?: CustomerConsentsPayload;
  },
) => {
  if (!contextId || !otp) return;

  try {
    const payload = {
      context_id: contextId,
      otp: otp,
      skip_token_generation: skipTokenGeneration,
      ...(additionalPayload && { ...additionalPayload }),
    };

    const response = await postRequest(`/otp/verify`, payload, "AUTH");
    return response;
  } catch (e: any) {
    console.error(e);
    throw e;
  }
};

export const resentOtp = async (contextId: string, channel: OTPChannel) => {
  if (!contextId) return;

  try {
    const payload = {
      context_id: contextId,
      preferred_channel: channel,
    };

    const response = await postRequest(`/otp/resend`, payload, "AUTH");
    return response;
  } catch (e: any) {
    console.error(e);
    throw e;
  }
};

/**
 * Creates a Shopify draft order for the given checkout + phone so the shopper can
 * continue as a guest without completing OTP verification. Returns the Shopify
 * invoice URL to redirect to, or undefined if the API did not return one.
 */
export const createGuestDraftOrder = async (
  checkoutId: string,
  phone: string,
): Promise<string | undefined> => {
  const response = await postRequest(`/draft-order/${checkoutId}`, { phone }, "KRATOS_PUBLIC");
  return response?.invoice_url as string | undefined;
};

export const isAccessTokenExpiringInNextFiveMinutesOrExpired = () => {
  const authToken = getAccessToken();
  try {
    if (!authToken) {
      return true;
    }
    const decodedToken = jwtDecode(authToken);
    const tokenExpiry = decodedToken.exp ? decodedToken.exp : 0;
    const currentTime = Date.now() / 1000;
    const timeUntilExpiry = tokenExpiry - currentTime;
    if (timeUntilExpiry > 5 * 60) {
      return false;
    }
    return true;
  } catch (e) {
    console.error("Error decoding token: ", e);
    return true;
  }
};

export const refreshExpiredIdToken = async (
  refreshTokenId?: string,
  isForceRefresh?: boolean,
): Promise<{ isLoggedIn: boolean; newTokensGenerated?: boolean; error?: string }> => {
  const refreshToken = refreshTokenId ?? getRefreshToken();
  const isTokenExpiringInNextFiveMinutesOrExpired = isAccessTokenExpiringInNextFiveMinutesOrExpired();

  if (!Boolean(isForceRefresh) && !isTokenExpiringInNextFiveMinutesOrExpired)
    return {
      isLoggedIn: true,
    };

  if (!Boolean(refreshToken) && isTokenExpiringInNextFiveMinutesOrExpired)
    return {
      isLoggedIn: false,
      error: "No Refresh Token, Expire in 5 minutes",
    };

  try {
    const payload = {
      refresh_token: refreshToken,
    };

    const response = await postRequest(`/user-token/refresh`, payload, "AUTH");
    if (!response)
      return {
        isLoggedIn: false,
      };
    const newAuthToken = response.access_token;
    const newRefreshToken = response.refresh_token;
    const newAuthTokenExpiry = response.access_token_expires_at;
    const newRefreshTokenExpiry = response?.refresh_token_expires_at;
    setAuthCookies(newAuthToken, newRefreshToken, newAuthTokenExpiry, newRefreshTokenExpiry);
    setAuthCookiesInLocalStorage(newAuthToken, newRefreshToken, newAuthTokenExpiry, newRefreshTokenExpiry);
    return {
      isLoggedIn: true,
      newTokensGenerated: true,
    };
  } catch (e: any) {
    Cookies.remove(constants.AUTH_COOKIE_CLIENT, { path: "/", sameSite: "none", secure: true });
    Cookies.remove(constants.REFRESH_TOKEN_COOKIE_CLIENT, { path: "/", sameSite: "none", secure: true });
    localStorage.removeItem(constants.ORDER_HISTORY_COOKIE_CLIENT);
    clearAuthLocalStorage();
    console.error(e);
    return {
      isLoggedIn: false,
      error: e?.response?.data?.error ?? "Refresh call failed",
    };
  }
};

export const setAuthCookies = (
  idToken: string,
  refreshToken: string,
  authExpiry?: number,
  refreshExpiry?: number,
) => {
  const authExpiresDate = authExpiry && new Date(authExpiry);
  const refreshExpiresDate = refreshExpiry && new Date(refreshExpiry);

  try {
    Cookies.set(constants.AUTH_COOKIE_CLIENT, idToken, {
      expires: authExpiresDate ?? constants.AUTH_COOKIE_CLIENT_EXPIRY,
      sameSite: "none",
      secure: true,
    });

    if (Boolean(refreshToken)) {
      Cookies.set(constants.REFRESH_TOKEN_COOKIE_CLIENT, refreshToken, {
        expires: refreshExpiresDate ?? constants.REFRESH_TOKEN_COOKIE_CLIENT_EXPIRY,
        sameSite: "none",
        secure: true,
      });
    } else {
      Cookies.set(constants.REFRESH_TOKEN_COOKIE_CLIENT, refreshToken, {
        expires: new Date(0),
        sameSite: "none",
        secure: true,
      });
      Cookies.remove(constants.REFRESH_TOKEN_COOKIE_CLIENT, { path: "/", sameSite: "none", secure: true });
    }
  } catch (e: any) {
    console.error(e);
    throw new Error(e);
  }
};

export const setAuthCookiesInLocalStorage = (
  idToken: string,
  refreshToken: string,
  authExpiry?: number,
  refreshExpiry?: number,
) => {
  const authExpiresDate = authExpiry && new Date(authExpiry);
  const refreshExpiresDate = refreshExpiry && new Date(refreshExpiry);
  localStorage.setItem(constants.AUTH_COOKIE_CLIENT, idToken);
  localStorage.setItem(constants.REFRESH_TOKEN_COOKIE_CLIENT, refreshToken);
  localStorage.setItem(constants.LOCAL_STORAGE_AUTH_COOKIE_CLIENT_EXPIRY, authExpiresDate?.toString() ?? "");
  localStorage.setItem(
    constants.LOCAL_STORAGE_REFRESH_TOKEN_COOKIE_CLIENT_EXPIRY,
    refreshExpiresDate?.toString() ?? "",
  );
};

export const clearAuthLocalStorage = () => {
  localStorage.removeItem(constants.AUTH_COOKIE_CLIENT);
  localStorage.removeItem(constants.REFRESH_TOKEN_COOKIE_CLIENT);
  localStorage.removeItem(constants.LOCAL_STORAGE_AUTH_COOKIE_CLIENT_EXPIRY);
  localStorage.removeItem(constants.LOCAL_STORAGE_REFRESH_TOKEN_COOKIE_CLIENT_EXPIRY);
};

/**
 * Stores the guest token of two step checkout. localStorage is the fallback for when the cookie is
 * dropped because third party cookies are blocked.
 */
export const setTwoStepAuthToken = (authToken: string, expiry?: number) => {
  Cookies.set(constants.TWO_STEP_AUTH_COOKIE_CLIENT, authToken, {
    expires: expiry,
    sameSite: "none",
    secure: true,
  });
  localStorage.setItem(constants.TWO_STEP_AUTH_COOKIE_CLIENT, authToken);
  localStorage.setItem(
    constants.LOCAL_STORAGE_TWO_STEP_AUTH_EXPIRY,
    expiry ? new Date(expiry).toString() : "",
  );
};

export const getTwoStepAuthToken = () => {
  if (Boolean(Cookies.get(constants.TWO_STEP_AUTH_COOKIE_CLIENT))) {
    return Cookies.get(constants.TWO_STEP_AUTH_COOKIE_CLIENT);
  }
  return getUnexpiredLocalStorageItem(
    constants.TWO_STEP_AUTH_COOKIE_CLIENT,
    constants.LOCAL_STORAGE_TWO_STEP_AUTH_EXPIRY,
  );
};

export const clearTwoStepAuthToken = () => {
  Cookies.remove(constants.TWO_STEP_AUTH_COOKIE_CLIENT);
  localStorage.removeItem(constants.TWO_STEP_AUTH_COOKIE_CLIENT);
  localStorage.removeItem(constants.LOCAL_STORAGE_TWO_STEP_AUTH_EXPIRY);
};

const getUnexpiredLocalStorageItem = (key: string, expiryKey: string) => {
  const value = localStorage.getItem(key);
  const expiry = localStorage.getItem(expiryKey);
  if (Boolean(value) && expiry && Date.now() < new Date(expiry).getTime()) {
    return value;
  }
  return "";
};

export const expireFloAuthCookies = () => {
  Cookies.set(constants.AUTH_COOKIE_CLIENT, "", {
    expires: new Date(0),
    sameSite: "none",
    secure: true,
  });
  Cookies.set(constants.REFRESH_TOKEN_COOKIE_CLIENT, "", {
    expires: new Date(0),
    sameSite: "none",
    secure: true,
  });
};

export const getAccessToken = () => {
  if (Boolean(Cookies.get(constants.AUTH_COOKIE_CLIENT))) {
    return Cookies.get(constants.AUTH_COOKIE_CLIENT);
  }
  const localStorageToken = getUnexpiredLocalStorageItem(
    constants.AUTH_COOKIE_CLIENT,
    constants.LOCAL_STORAGE_AUTH_COOKIE_CLIENT_EXPIRY,
  );
  if (Boolean(localStorageToken)) {
    return localStorageToken;
  }
  return getTwoStepAuthToken() ?? "";
};

export const getRefreshToken = () => {
  if (Boolean(Cookies.get(constants.REFRESH_TOKEN_COOKIE_CLIENT))) {
    return Cookies.get(constants.REFRESH_TOKEN_COOKIE_CLIENT);
  }
  return getUnexpiredLocalStorageItem(
    constants.REFRESH_TOKEN_COOKIE_CLIENT,
    constants.LOCAL_STORAGE_REFRESH_TOKEN_COOKIE_CLIENT_EXPIRY,
  );
};

export function getAuthCookies() {
  const hasLocalStorageAuth =
    Boolean(localStorage.getItem(constants.AUTH_COOKIE_CLIENT)) &&
    Boolean(localStorage.getItem(constants.REFRESH_TOKEN_COOKIE_CLIENT));
  if (!isThirdPartyCookieEnabled({ checkPartitionedBrowser: false }) && !hasLocalStorageAuth) {
    return;
  }
  const authCookie = getAccessToken();
  const authCookieExpiry = localStorage.getItem(constants.LOCAL_STORAGE_AUTH_COOKIE_CLIENT_EXPIRY);
  const refreshCookie = getRefreshToken();
  const refreshCookieExpiry = localStorage.getItem(
    constants.LOCAL_STORAGE_REFRESH_TOKEN_COOKIE_CLIENT_EXPIRY,
  );
  if (!authCookie) {
    return;
  }
  return {
    authCookie,
    authCookieExpiry,
    refreshCookie,
    refreshCookieExpiry,
  };
}
